import GeneralHands from '@/services/hands/GeneralHands';
import FiveCardHands from '@/services/five-card-hands/FiveCardHands';
import flopInfoHelpers from '@/services/flop-info-helpers';
import constants from '@/services/constants';
import utilities from '@/services/utilities/utilities';
import _ from 'lodash';

export default class PlayerState {
  constructor(stateName) {
    this.name = stateName;
    this.generalHands = new GeneralHands();
    this.equity = 0;
    this.winPercentage = 0;
    this.tiePercentage = 0;

    if (stateName !== 'afterPreflop') {
      this.generalHands.initializeAsBelongingToPlayerStateOtherThanFirstPlayerState();
      this._hitPercentage = 0;
      this.fiveCardHands = new FiveCardHands();
      this.handAndDrawInfo = [];
      this.lastSimulation = {
        generalHands: null,
        nameOfStateUsedForSimulation: null,
        presetCommunityCards: null,
        presetHoleCards: null,
        exhaustive: null,
      };
      this.hitPercentageBeingComputed = null;
      this.nextState = null;
    }
  };

  // iterators
  forCurrStateAndFutureStates(cb) {
    let state = this;

    do {
      cb(state);
      state = state.nextState;
    } while (state);
  };
  forEachFutureState(cb) {
    let state = this.nextState;

    while (state) {
      cb(state);
      state = state.nextState;
    }
  };

  // getters
  get hitPercentage() {
    if (this.lastSimulation.exhaustive) {
      return ((this.generalHands.numberOfSpecificHandsInRange / this.generalHands.numberOfPossibleSpecificHands) * 100).roundToTwoDecimalPlaces();
    }

    return this._hitPercentage;
  };
  get displayName() {
    if (this.name === 'afterPreflop') {
      return 'Preflop';
    } else if (this.name === 'afterFlop') {
      return 'Flop';
    } else if (this.name === 'afterTurn') {
      return 'Turn';
    } else if (this.name === 'afterRiver') {
      return 'River';
    }
  };
  get anyVisibleDraws() {
    let retVal = false;

    this.fiveCardHands.forEachVisibleLeaf(function () {
      retVal = true;
    });

    return retVal;
  };
  get simulateTo() {
    if (this.name === 'afterFlop') {
      return 'flop';
    } else if (this.name === 'afterTurn') {
      return 'turn';
    } else if (this.name === 'afterRiver') {
      return 'river';
    }
  };
  get equityString() {
    return this.equity.toFixed(2);
  };
  get winPercentageString() {
    return this.winPercentage.toFixed(2);
  };
  get tiePercentageString() {
    return this.tiePercentage.toFixed(2);
  };
  get hasSimulationBeenRun() {
    let hasSimulationBeenRun = true;

    this.fiveCardHands.forEachLeaf(function (leaf) {
      if (leaf._probability === null) {
        hasSimulationBeenRun = false;
      }
    });

    return hasSimulationBeenRun;
  };

  // five card hands
  getFiveCardHandsThatAreCheckedByDefault(presetCommunityCards, opponentTightness, settings) {
    let defaultChecksObjFromSettings = settings.hitCalculator.defaultChecks;

    if (this.name === 'afterFlop') {
      if (presetCommunityCards.flopSet) {
        return this._getFiveCardHandsThatAreCheckedByDefaultForPresetFlop(presetCommunityCards, defaultChecksObjFromSettings, opponentTightness);
      } else {
        return defaultChecksObjFromSettings.afterFlop.default[opponentTightness];
      }
    } else {
      return defaultChecksObjFromSettings[this.name][opponentTightness];
    }
  };
  _getFiveCardHandsThatAreCheckedByDefaultForPresetFlop(presetCommunityCards, defaultChecksObjFromSettings, opponentTightness) {
    let flopCards = presetCommunityCards.flopArray;
    let flopTone = flopInfoHelpers.getFlopTone(flopCards);
    let flopTexture = flopInfoHelpers.getFlopTexture(flopCards);
    return defaultChecksObjFromSettings.afterFlop[flopTone][flopTexture][opponentTightness];
  };
  areFiveCardHandPercentagesUpToDate(player, currentPresetCommunityCards, currentPresetHoleCards) {
    if (this.lastSimulation.generalHands === null) {
      return true;
    }

    let presetCommunityCardsSameAsPresetCommunityCardsUsedForLastSimulation = this._arePresetCommunityCardsSameAsPresetCommunityCardsUsedForLastSimulation(currentPresetCommunityCards);
    let presetHoleCardsSameAsPresetHoleCardsUsedForLastSimulation = this._arePresetHoleCardsSameAsPresetHoleCardsUsedForLastSimulation(currentPresetHoleCards);

    if (this.lastSimulation.exhaustive) {
      return (
        presetCommunityCardsSameAsPresetCommunityCardsUsedForLastSimulation &&
        this._isRangeSameAsRangeUsedForLastSimulation(player, currentPresetCommunityCards, true)
      );
    }

    else if (!this.lastSimulation.exhaustive) {
      return (
        presetCommunityCardsSameAsPresetCommunityCardsUsedForLastSimulation &&
        presetHoleCardsSameAsPresetHoleCardsUsedForLastSimulation &&
        this._isRangeSameAsRangeUsedForLastSimulation(player, currentPresetCommunityCards)
      );
    }
  };
  _arePresetCommunityCardsSameAsPresetCommunityCardsUsedForLastSimulation(currentPresetCommunityCards) {
    if (this.name === 'afterFlop') {
      return (
        (currentPresetCommunityCards.flop1 === this.lastSimulation.presetCommunityCards.flop1) &&
        (currentPresetCommunityCards.flop2 === this.lastSimulation.presetCommunityCards.flop2) &&
        (currentPresetCommunityCards.flop3 === this.lastSimulation.presetCommunityCards.flop3)
      );
    }

    else if (this.name === 'afterTurn') {
      return (
        (currentPresetCommunityCards.flop1 === this.lastSimulation.presetCommunityCards.flop1) &&
        (currentPresetCommunityCards.flop2 === this.lastSimulation.presetCommunityCards.flop2) &&
        (currentPresetCommunityCards.flop3 === this.lastSimulation.presetCommunityCards.flop3) &&
        (currentPresetCommunityCards.turn === this.lastSimulation.presetCommunityCards.turn)
      );
    }

    else if (this.name === 'afterRiver') {
      return (
        (currentPresetCommunityCards.flop1 === this.lastSimulation.presetCommunityCards.flop1) &&
        (currentPresetCommunityCards.flop2 === this.lastSimulation.presetCommunityCards.flop2) &&
        (currentPresetCommunityCards.flop3 === this.lastSimulation.presetCommunityCards.flop3) &&
        (currentPresetCommunityCards.turn === this.lastSimulation.presetCommunityCards.turn) &&
        (currentPresetCommunityCards.river === this.lastSimulation.presetCommunityCards.river)
      );
    }
  };
  _arePresetHoleCardsSameAsPresetHoleCardsUsedForLastSimulation(currentPresetHoleCards) {
    return (
      (currentPresetHoleCards.holeCardOne === this.lastSimulation.presetHoleCards.holeCardOne) &&
      (currentPresetHoleCards.holeCardTwo === this.lastSimulation.presetHoleCards.holeCardTwo)
    );
  };
  _isRangeSameAsRangeUsedForLastSimulation(player, presetCommunityCards, hack) {
    let generalHandsThatWouldBeUsedForThisSimulation = utilities.hands.getGeneralHandsToUseForSimulation(player, presetCommunityCards, this.simulateTo);
    return generalHandsThatWouldBeUsedForThisSimulation.matchesOtherRange(this.lastSimulation.generalHands, hack);
  };
  isHitPercentageUpToDate(player, currentPresetCommunityCards, currentPresetHoleCards) {
    return (
      this._canHitPercentageBeComputedWithPresetCommunityCards(currentPresetCommunityCards) ||
      this.areFiveCardHandPercentagesUpToDate(player, currentPresetCommunityCards, currentPresetHoleCards)
    );
  };
  _canHitPercentageBeComputedWithPresetCommunityCards(presetCommunityCards) {
    if (this.simulateTo === 'flop') {
      return presetCommunityCards.flopSet;
    } else if (this.simulateTo === 'turn') {
      return presetCommunityCards.turnSet;
    } else if (this.simulateTo === 'river') {
      return presetCommunityCards.riverSet;
    }
  };

  // hands
  setWeightOfSpecificHand(specificHandString, newWeight) {
    let specificHandOfCurrentState = this.generalHands.find(specificHandString);
    let specificHandOfNextState;

    if (newWeight > specificHandOfCurrentState.maxPossibleWeight) {
      newWeight = specificHandOfCurrentState.maxPossibleWeight;
    }

    this.generalHands.numberOfSpecificHandsInRange -= specificHandOfCurrentState.weight / 100;
    this.generalHands.numberOfSpecificHandsInRange += newWeight / 100;
    specificHandOfCurrentState.weight = newWeight;

    if (this.nextState) {
      specificHandOfNextState = this.nextState.generalHands.find(specificHandString);

      if (specificHandOfNextState.manuallySet && newWeight === 0) {
        specificHandOfNextState.manuallySet = false;
      }

      this.nextState.generalHands.numberOfPossibleSpecificHands -= specificHandOfNextState.maxPossibleWeight / 100;
      this.nextState.generalHands.numberOfPossibleSpecificHands += newWeight / 100;
      specificHandOfNextState.maxPossibleWeight = newWeight;
    }
  };
  setRangeAccordingToFilters(previousRange, presetCommunityCards, shortDeck) {
    let currSpecificHand;
    let weightToApply;

    if (this.name === 'afterPreflop') {
      throw new Error('Trying to set the afterPreflop range according to the filters.');
    }

    previousRange.forEachSpecificHand(function (previousSpecificHand, specificHandString) {
      currSpecificHand = this.generalHands.find(specificHandString);

      if (!previousSpecificHand.possible || previousSpecificHand.weight === 0) {
        this.setWeightOfSpecificHand(specificHandString, 0);
        this.generalHands.numberOfPossibleSpecificHands -= currSpecificHand.maxPossibleWeight / 100;
        currSpecificHand.maxPossibleWeight = 0;
        currSpecificHand.manuallySet = false;
      }

      else {
        weightToApply = currSpecificHand.getWeightToApply(this, presetCommunityCards, previousSpecificHand.weight, shortDeck);
        this.setWeightOfSpecificHand(specificHandString, weightToApply);
      }
    }, this);
  };

  // other
  isRangePossible(presetCommunityCards) {
    if (this.name === 'afterFlop') {
      if (presetCommunityCards.flopSet) {
        return true;
      }
    }

    if (this.name === 'afterTurn') {
      if (presetCommunityCards.turnSet) {
        return true;
      }
    }

    if (this.name === 'afterRiver') {
      if (presetCommunityCards.riverSet) {
        return true;
      }
    }

    return false;
  };

  // hit percentage for different ranges
  get hitPercentageForDifferentRanges() {
    return constants.orderedFiveCardHandIds
      .filter(function (fiveCardHandId) {
        return this.fiveCardHands.find(fiveCardHandId).matchingSpecificHands.length > 0;
      }, this)
      .map(function (fiveCardHandId) {
        return {
          cutoffHand: this.fiveCardHands.find(fiveCardHandId),
          hitPercentage: this._getHitPercentageForRange(fiveCardHandId),
        };
      }, this)
    ;
  };
  _getHitPercentageForRange(cutoffHand) {
    let fiveCardHandIdsInRange = constants.orderedFiveCardHandIds.getElementsUpToAndIncluding(cutoffHand);
    let specificHandStringsInRange = this.__getSpecificHandStringsInRange(fiveCardHandIdsInRange);
    let numberOfSpecificHandsInRange = this.__getNumberOfSpecificHandsInRange(specificHandStringsInRange);

    return numberOfSpecificHandsInRange / this.generalHands.numberOfPossibleSpecificHands;
  };
  __getSpecificHandStringsInRange(fiveCardHandIdsInRange) {
    let specificHandStringsInRange = [];

    this.fiveCardHands.forEachVisibleNodeInList(fiveCardHandIdsInRange, function (fiveCardHand) {
      specificHandStringsInRange = [...specificHandStringsInRange, ...fiveCardHand.matchingSpecificHands];
    });
    specificHandStringsInRange = _.uniq(specificHandStringsInRange);

    return specificHandStringsInRange;
  };
  __getNumberOfSpecificHandsInRange(specificHandStringsInRange) {
    return specificHandStringsInRange
      .map(function (specificHandString) {
        return this.generalHands.find(specificHandString);
      }, this)
      .reduce(function (numberOfSpecificHandsInRange, specificHand) {
        return numberOfSpecificHandsInRange + specificHand.maxPossibleWeight;
      }, 0)
    ;
  };
  get foldEquityAgainstDifferentRanges() {
    return this.hitPercentageForDifferentRanges.map(function ({ cutoffHand, hitPercentage }) {
      return {
        cutoffHand: cutoffHand,
        foldEquity: (100 - hitPercentage).roundToTwoDecimalPlaces(),
      };
    });
  };
  getIndexOfCutoffHandForBluffing(breakevenPercentage) {
    return this.foldEquityAgainstDifferentRanges.reduce(function (indexOfCutoffHandForBluffing, { cutoffHand, foldEquity }, index) {
      if (foldEquity > breakevenPercentage) {
        return index;
      } else {
        return indexOfCutoffHandForBluffing;
      }
    }, null);
  };
  getCutoffHandForBluffing(breakevenPercentage) {
    let indexOfCutoffHandForBluffing = this.getIndexOfCutoffHandForBluffing(breakevenPercentage);

    return this.foldEquityAgainstDifferentRanges[indexOfCutoffHandForBluffing].cutoffHand;
  };
  getCondensedFoldEquityAgainstDifferentRanges(breakevenPercentage) {
    let indexes = [];
    let indexOfCutoffHandForBluffing = this.getIndexOfCutoffHandForBluffing(breakevenPercentage);
    let lastPossibleIndex = this.foldEquityAgainstDifferentRanges.length - 1;

    indexes.push(indexOfCutoffHandForBluffing - 3);
    indexes.push(indexOfCutoffHandForBluffing - 2);
    indexes.push(indexOfCutoffHandForBluffing - 1);
    indexes.push(indexOfCutoffHandForBluffing);
    indexes.push(indexOfCutoffHandForBluffing + 1);
    indexes.push(indexOfCutoffHandForBluffing + 2);
    indexes.push(indexOfCutoffHandForBluffing + 3);
    indexes = indexes.filter(function (index) {
      return (index >= 0 && index <= lastPossibleIndex);
    });

    return indexes.map(function (index) {
      return this.foldEquityAgainstDifferentRanges[index];
    }, this);
  };
};



// WEBPACK FOOTER //
// ./src/services/player/PlayerState.js